import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {ehCelular, EstadoOnboardingPainel, OnboardingPainelService, PedidoTesteOnboarding} from '../onboarding-painel.service';

const QRCode = require('qrcode');

const NOVO = 0, EM_PREPARO = 1, PRONTO = 2, SAIU = 3, ENTREGUE = 4, CANCELADO = 5;
const RETIRAR = 1;

/**
 * Passo 6 · Seu primeiro pedido. O dono pede na própria loja (link com token: passa com a loja fechada) e tira o
 * pedido aqui, com as mesmas ações do painel: aceitar, pronto/saiu, entregue. Concluir cancela o teste (sai das
 * vendas), passa a loja para a próxima fase e garante o bônus.
 */
@Component({
  selector: 'app-passo-teste',
  templateUrl: './passo-teste.component.html',
  styleUrls: ['./passo-teste.component.scss']
})
export class PassoTesteComponent implements OnInit, OnDestroy {
  @Input() estado: EstadoOnboardingPainel;
  @Output() concluido = new EventEmitter<EstadoOnboardingPainel>();
  @Output() atualizou = new EventEmitter<EstadoOnboardingPainel>();

  link = '';
  qr = '';
  gerandoLink = false;
  copiado = false;
  agindo = false;
  concluindo = false;
  erro = '';
  /** No celular o dono já está no aparelho que pede: sem QR, e o som precisa ser destravado num toque. */
  readonly celular = ehCelular();
  private espera: any;
  private chegouAntes = false;
  private audio: AudioContext | null = null;

  constructor(private onboarding: OnboardingPainelService) {}

  /** Ao voltar da aba da loja (o celular congela o relógio da aba de trás), confere na hora. */
  private aoVoltar = () => { if (document.visibilityState === 'visible' && this.espera) this.atualize(); };

  ngOnInit(): void {
    document.addEventListener('visibilitychange', this.aoVoltar);
    this.chegouAntes = !!this.pedido;
    if (!this.podeTestar) return;
    if (!this.pedido || this.pedido.status === CANCELADO) this.gereLink();
    else this.acompanhe();
  }

  ngOnDestroy(): void {
    document.removeEventListener('visibilitychange', this.aoVoltar);
    this.pareDeEsperar();
    if (this.audio) this.audio.close().catch(() => {});
  }

  get pedido(): PedidoTesteOnboarding | null { return this.estado?.pedidoTeste || null; }

  get temProdutoComPreco(): boolean { return !!this.estado?.passos?.find(p => p.id === 'cardapio')?.feito; }

  /** Sem entrega nem retirada ativa o checkout da loja não fecha: o teste espera o passo da loja. */
  get temComoReceber(): boolean { return !!(this.estado?.loja?.delivery || this.estado?.loja?.retirada); }

  get podeTestar(): boolean { return this.temProdutoComPreco && this.temComoReceber; }

  get pendentes(): string[] {
    return (this.estado?.passos || []).filter(p => !['teste', 'cardapio', 'loja'].includes(p.id) && !p.feito).map(p => p.nome.toLowerCase());
  }

  get retirada(): boolean { return Number(this.pedido?.formaDeEntregaId) === RETIRAR; }

  get total(): number { return Number(this.pedido?.valor || 0) + Number(this.pedido?.taxaEntrega || 0); }

  get etapas(): string[] { return ['Novo', 'Em preparo', this.retirada ? 'Pronto' : 'Saiu', 'Entregue']; }

  get indiceEtapa(): number {
    const s = this.pedido?.status;
    if (s === NOVO) return 0;
    if (s === EM_PREPARO) return 1;
    if (s === PRONTO || s === SAIU) return 2;
    return 3;
  }

  get guia(): {titulo: string; texto: string; botao: string} | null {
    const s = this.pedido?.status;
    if (s === NOVO) return {titulo: 'Pedido novo!', texto: 'Confira os itens e toque em Aceitar. O cliente recebe no WhatsApp que o pedido foi aceito.', botao: 'Aceitar pedido'};
    if (s === EM_PREPARO) return this.retirada
      ? {titulo: 'Em preparo.', texto: 'Quando estiver pronto, toque em Pronto para retirar. O cliente é avisado para buscar.', botao: 'Pronto para retirar'}
      : {titulo: 'Em preparo.', texto: 'Quando o entregador sair, toque em Saiu para entrega. O cliente é avisado.', botao: 'Saiu para entrega'};
    if (s === PRONTO || s === SAIU) return {titulo: 'Quase lá.', texto: 'Quando o cliente receber, toque em Entregue para fechar o pedido.', botao: 'Entregue'};
    return null;
  }

  gereLink(): void {
    this.gerandoLink = true;
    this.erro = '';
    this.onboarding.linkPedidoTeste().then(r => {
      this.link = r.link;
      QRCode.toDataURL(r.link, {margin: 1, width: 320}).then((url: string) => this.qr = url).catch(() => {});
      this.acompanhe();
    }).catch(e => this.erro = OnboardingPainelService.mensagem(e, 'Não foi possível gerar o link do teste.'))
      .finally(() => this.gerandoLink = false);
  }

  /** Sempre ativo: sem o link pronto, gera e abre (a aba abre já, para o navegador não bloquear o popup). */
  abraLoja(): void {
    this.destraveSom();
    this.onboarding.registre('LINK_TESTE', 'abriu');
    if (this.link) { window.open(this.link, '_blank', 'noopener'); return; }
    const aba = window.open('', '_blank');
    if (!this.gerandoLink) this.gereLink();
    const espere = setInterval(() => {
      if (!this.link && this.gerandoLink) return;
      clearInterval(espere);
      if (aba && this.link) aba.location.href = this.link; else if (aba) aba.close();
    }, 300);
  }

  copie(): void {
    this.destraveSom();
    if (!this.link) { if (!this.gerandoLink) this.gereLink(); return; }
    const feito = () => { this.copiado = true; setTimeout(() => this.copiado = false, 1800); };
    try {
      navigator.clipboard.writeText(this.link).then(feito, feito);
    } catch (e) { feito(); }
  }

  /** A tela se atualiza sozinha: primeiro esperando o pedido chegar, depois acompanhando o status. */
  private acompanhe(): void {
    this.pareDeEsperar();
    this.espera = setInterval(() => this.atualize(), 3000);
  }

  private pareDeEsperar(): void {
    if (this.espera) clearInterval(this.espera);
    this.espera = null;
  }

  private atualize(): Promise<void> {
    return this.onboarding.estado().then(estado => {
      const tinhaPedido = !!this.pedido && this.pedido.status !== CANCELADO;
      this.estado = estado;
      this.atualizou.emit(estado);
      if (!tinhaPedido && this.pedido && this.pedido.status !== CANCELADO && !this.chegouAntes) this.toque();
      if (this.pedido && this.pedido.status !== CANCELADO) this.chegouAntes = true;
      if (this.pedido?.status === ENTREGUE) this.pareDeEsperar();
    }).catch(() => {});
  }

  async avance(): Promise<void> {
    const p = this.pedido;
    if (!p || this.agindo) return;
    this.agindo = true;
    this.erro = '';
    try {
      if (p.status === NOVO) {
        if (!p.aceito) await this.onboarding.aceitePedido(p.guid);
        await this.onboarding.mudeStatusPedido(p.guid, EM_PREPARO);
      } else if (p.status === EM_PREPARO) {
        await this.onboarding.mudeStatusPedido(p.guid, this.retirada ? PRONTO : SAIU);
      } else if (p.status === PRONTO || p.status === SAIU) {
        await this.onboarding.mudeStatusPedido(p.guid, ENTREGUE);
      }
      await this.atualize();
    } catch (e) {
      this.erro = OnboardingPainelService.mensagem(e, 'Não foi possível atualizar o pedido. Tente de novo.');
    } finally {
      this.agindo = false;
    }
  }

  conclua(): void {
    if (this.concluindo) return;
    this.concluindo = true;
    this.erro = '';
    this.onboarding.conclua().then(estado => this.concluido.emit(estado))
      .catch(e => this.erro = OnboardingPainelService.mensagem(e, 'Não foi possível concluir. Tente de novo.'))
      .finally(() => this.concluindo = false);
  }

  /** iPhone só libera áudio criado ou retomado dentro de um toque: sem isso o pedido chega mudo. */
  private destraveSom(): void {
    try {
      const Contexto = (window as any).AudioContext || (window as any).webkitAudioContext;
      this.audio = this.audio || new Contexto();
      if (this.audio.state !== 'running') Promise.resolve(this.audio.resume()).catch(() => {});
    } catch (e) {}
  }

  private toque(): void {
    try {
      const Contexto = (window as any).AudioContext || (window as any).webkitAudioContext;
      this.audio = this.audio || new Contexto();
      if (this.audio.state !== 'running') Promise.resolve(this.audio.resume()).catch(() => {});
      const t = this.audio.currentTime;
      [[880, 0], [1175, .16], [880, .42], [1175, .58]].forEach(([freq, atraso]) => {
        const osc = this.audio.createOscillator(), ganho = this.audio.createGain();
        osc.frequency.value = freq;
        ganho.gain.setValueAtTime(0, t + atraso);
        ganho.gain.linearRampToValueAtTime(.25, t + atraso + .02);
        ganho.gain.exponentialRampToValueAtTime(.001, t + atraso + .2);
        osc.connect(ganho).connect(this.audio.destination);
        osc.start(t + atraso);
        osc.stop(t + atraso + .22);
      });
    } catch (e) {}
  }
}
